/* Pipeline 172X: sign-in page
   Local mode uses the custom passwordless/OAuth API boundary.
   Hosted mode uses Supabase PKCE/email providers and then backend /me.
   Security: generic errors, no account enumeration, no token rendering. */

import { useState } from "react";
import {
  AuthShell,
  ProviderButton,
  OrDivider,
  EmailInput,
  PrimaryAuthButton,
  AuthLink,
  VerificationPending,
  OAuthErrorState,
  SessionExpiredBanner,
} from "../components/auth/AuthComponents";
import { authGateway } from "../lib/AuthGateway";
import { ApiClientError } from "../lib/apiClient";
import { useAuthConfiguration } from "../app/useAuthConfiguration";
import type { AuthUser } from "../lib/AuthGateway";
import type { SigninStep, NavCallbacks } from "../types/auth";

interface SigninPageProps extends NavCallbacks {
  sessionExpired?: boolean;
  onSignedIn?: (user: AuthUser) => Promise<void> | void;
}

type SigninOAuthError =
  | "cancelled"
  | "provider-denied"
  | "provider-unavailable"
  | "missing-email"
  | "existing-account";

export default function SigninPage({
  goTo,
  goBack,
  sessionExpired = false,
  onSignedIn,
}: SigninPageProps) {
  const [step, setStep] = useState<SigninStep>(
    sessionExpired ? "select-method" : "select-method",
  );
  const [showSessionBanner, setShowSessionBanner] = useState(sessionExpired);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [oauthProvider, setOauthProvider] = useState<
    "google" | "linkedin" | null
  >(null);
  const [oauthError, setOauthError] = useState<SigninOAuthError | null>(null);
  const [verificationSent, setVerificationSent] = useState(false);
  const [otpRequested, setOtpRequested] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpStatus, setOtpStatus] = useState<string | null>(null);
  const [developmentVerificationUrl, setDevelopmentVerificationUrl] = useState<
    string | null
  >(null);
  const { configuration, configurationError } = useAuthConfiguration();

  const providerStatusPending = !configuration && !configurationError;
  const googleDisabled = !configuration?.googleEnabled;
  const linkedinDisabled = !configuration?.linkedinEnabled;
  const emailDisabled = !configuration?.emailEnabled;

  function isValidEmail(e: string) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(e);
  }

  /* ── OAuth ────────────────────────────────────────────────────── */
  async function handleOAuth(provider: "google" | "linkedin") {
    setOauthProvider(provider);
    setStep("oauth-loading");
    setOauthError(null);
    try {
      const { redirectUrl } = await authGateway.startOAuth(provider);
      const destination = new URL(redirectUrl, window.location.origin);
      if (!["http:", "https:"].includes(destination.protocol)) {
        throw new Error("Unsupported OAuth redirect protocol");
      }
      window.location.assign(destination);
    } catch (error) {
      const code =
        error instanceof ApiClientError ? error.problem.code : "unknown";
      const supportedErrors: SigninOAuthError[] = [
        "cancelled",
        "provider-denied",
        "provider-unavailable",
        "missing-email",
        "existing-account",
      ];
      setOauthError(
        supportedErrors.includes(code as SigninOAuthError)
          ? (code as SigninOAuthError)
          : "provider-unavailable",
      );
      setStep("oauth-error");
    }
  }

  function handleOAuthRetry() {
    if (oauthProvider) void handleOAuth(oauthProvider);
  }

  function handleOAuthAlternate() {
    setStep("select-method");
    setOauthError(null);
    setOauthProvider(null);
  }

  /* ── Email ────────────────────────────────────────────────────── */
  async function handleEmailContinue() {
    if (emailDisabled) {
      setEmailError("Email sign-in is not available in this environment.");
      return;
    }
    if (!isValidEmail(email)) {
      setEmailError("Enter a valid email address.");
      return;
    }
    setEmailError("");
    setOtpStatus(null);
    setIsLoading(true);
    try {
      const normalizedEmail = email.trim().toLowerCase();
      setEmail(normalizedEmail);
      if (configuration?.authMode === "supabase") {
        await authGateway.requestSignInOtp(normalizedEmail);
        setOtpRequested(true);
      } else {
        const result = await authGateway.submitEmail(normalizedEmail);
        setDevelopmentVerificationUrl(
          result.developmentVerificationUrl ?? null,
        );
        setVerificationSent(true);
      }
      setStep("enter-email");
    } catch (error) {
      setEmailError(
        error instanceof ApiClientError
          ? error.problem.detail
          : "We couldn’t send the sign-in code. Check your connection and retry.",
      );
    } finally {
      setIsLoading(false);
    }
  }

  async function handleOtpVerification() {
    if (!/^\d{6}$/.test(otp)) {
      setEmailError("Enter the six-digit code from your email.");
      return;
    }
    setEmailError("");
    setOtpStatus(null);
    setIsLoading(true);
    try {
      const user = await authGateway.verifySignInOtp(email, otp);
      setStep("success");
      if (onSignedIn) await onSignedIn(user);
      else goTo(user.onboardingComplete ? "app" : "onboarding");
    } catch (error) {
      setEmailError(
        error instanceof ApiClientError
          ? signInOtpProblem(error)
          : "We couldn’t verify this code. Retry or request a new code.",
      );
    } finally {
      setIsLoading(false);
    }
  }

  async function handleOtpResend() {
    setEmailError("");
    setOtpStatus(null);
    setIsLoading(true);
    try {
      await authGateway.requestSignInOtp(email);
      setOtp("");
      setOtpStatus("A new six-digit code was sent if this email can sign in.");
    } catch (error) {
      setEmailError(
        error instanceof ApiClientError
          ? error.problem.detail
          : "We couldn’t resend the code. Please retry.",
      );
    } finally {
      setIsLoading(false);
    }
  }

  /* ── Rendering ───────────────────────────────────────────────── */

  /* OAuth loading */
  if (step === "oauth-loading") {
    return (
      <AuthShell
        title={`Continuing with ${oauthProvider === "google" ? "Google" : "LinkedIn"}…`}
        onBack={goBack}
      >
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <div
            style={{
              width: 40,
              height: 40,
              border: "3px solid #e5e7eb",
              borderTopColor: "#1a3a5c",
              borderRadius: "50%",
              margin: "0 auto 20px",
              animation: "spin 0.9s linear infinite",
            }}
            role="status"
            aria-label="Signing in"
          />
          <button
            onClick={handleOAuthAlternate}
            style={{
              marginTop: 20,
              fontFamily: "Inter, sans-serif",
              fontSize: 12.5,
              color: "#1a3a5c",
              background: "none",
              border: "none",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            Cancel
          </button>
        </div>
      </AuthShell>
    );
  }

  /* OAuth error */
  if (step === "oauth-error" && oauthError) {
    return (
      <AuthShell title="Sign in" onBack={goBack}>
        <OAuthErrorState
          error={oauthError}
          onRetry={handleOAuthRetry}
          onAlternate={handleOAuthAlternate}
        />
      </AuthShell>
    );
  }

  /* Success */
  if (step === "success") {
    return (
      <AuthShell title="Signed in" subtitle="Taking you back…" onBack={goBack}>
        <div style={{ textAlign: "center", padding: "16px 0" }}>
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 14,
              background: "#f0fdf4",
              border: "1px solid #bbf7d0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto",
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M5 12.5L9.5 17l9.5-10"
                stroke="#15803d"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
      </AuthShell>
    );
  }

  /* Account suspended */
  if (step === "account-suspended") {
    return (
      <AuthShell title="Account unavailable" onBack={goBack}>
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 14,
              background: "#fef2f2",
              border: "1px solid #fecaca",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px",
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <circle
                cx="12"
                cy="12"
                r="9"
                stroke="#dc2626"
                strokeWidth="1.5"
              />
              <path
                d="M12 7v5M12 14.5v.5"
                stroke="#dc2626"
                strokeWidth="1.75"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <p
            style={{
              fontFamily: "Inter, sans-serif",
              fontSize: 13.5,
              color: "#4b5563",
              lineHeight: 1.6,
              marginBottom: 20,
            }}
          >
            This account is currently unavailable. Contact support if you
            believe this is in error.
          </p>
          <a
            href="mailto:hello@weare172x.com"
            style={{
              fontFamily: "Inter, sans-serif",
              fontSize: 13,
              color: "#1a3a5c",
              textDecoration: "underline",
            }}
          >
            Contact support
          </a>
        </div>
      </AuthShell>
    );
  }

  /* Email verification pending */
  if (otpRequested) {
    return (
      <AuthShell
        title="Enter your six-digit code"
        subtitle="Use the code sent to your email. It expires after 15 minutes."
        onBack={goBack}
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void handleOtpVerification();
          }}
          noValidate
          style={{ display: "flex", flexDirection: "column", gap: 14 }}
        >
          <label className="auth-field-label" htmlFor="signin-code-email">
            Email address
          </label>
          <input
            id="signin-code-email"
            className="auth-input"
            type="email"
            autoComplete="email"
            value={email}
            readOnly
          />
          <label className="auth-field-label" htmlFor="signin-code">
            Six-digit code
          </label>
          <input
            id="signin-code"
            className="auth-input auth-code-input"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            required
            autoFocus
            value={otp}
            onChange={(event) => setOtp(event.target.value.replace(/\D/g, ""))}
            aria-invalid={Boolean(emailError)}
            aria-describedby={emailError ? "signin-code-error" : undefined}
          />
          {emailError ? (
            <p id="signin-code-error" className="auth-error" role="alert">
              {emailError}
            </p>
          ) : null}
          {otpStatus ? (
            <p className="auth-supporting-copy" role="status">
              {otpStatus}
            </p>
          ) : null}
          <PrimaryAuthButton type="submit" isLoading={isLoading}>
            {isLoading ? "Verifying…" : "Verify and sign in"}
          </PrimaryAuthButton>
          <button
            className="auth-secondary-button"
            type="button"
            disabled={isLoading}
            onClick={() => void handleOtpResend()}
          >
            Send a new code
          </button>
          <AuthLink
            onClick={() => {
              setOtpRequested(false);
              setOtp("");
              setEmail("");
              setEmailError("");
            }}
          >
            Use a different email address
          </AuthLink>
        </form>
      </AuthShell>
    );
  }

  if (verificationSent) {
    return (
      <AuthShell title="Check your email" onBack={goBack}>
        <VerificationPending
          email={email}
          onResend={() => {
            setIsLoading(true);
            void authGateway
              .resendVerification(email)
              .then((result) => {
                setDevelopmentVerificationUrl(
                  result.developmentVerificationUrl ?? null,
                );
              })
              .catch((error: unknown) => {
                setEmailError(
                  error instanceof ApiClientError
                    ? error.problem.detail
                    : "We couldn’t resend the link. Please retry.",
                );
              })
              .finally(() => setIsLoading(false));
          }}
          onChangeEmail={() => {
            setVerificationSent(false);
            setDevelopmentVerificationUrl(null);
            setEmail("");
            setStep("select-method");
          }}
        />
        {developmentVerificationUrl && (
          <button
            type="button"
            onClick={() => window.location.assign(developmentVerificationUrl)}
            style={{
              width: "100%",
              marginTop: 16,
              border: "1px solid #d1d5db",
              borderRadius: 9,
              background: "#f9fafb",
              color: "#1a3a5c",
              padding: "10px 14px",
              cursor: "pointer",
            }}
          >
            Open local development verification link
          </button>
        )}
      </AuthShell>
    );
  }

  /* Email entry step */
  if (step === "enter-email") {
    return (
      <AuthShell title="Sign in with email" onBack={goBack}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void handleEmailContinue();
          }}
          noValidate
          style={{ display: "flex", flexDirection: "column", gap: 14 }}
        >
          <EmailInput
            value={email}
            onChange={setEmail}
            error={emailError}
            disabled={isLoading}
            autoFocus
          />
          <PrimaryAuthButton type="submit" isLoading={isLoading}>
            {isLoading
              ? configuration?.authMode === "supabase"
                ? "Sending code…"
                : "Sending link…"
              : configuration?.authMode === "supabase"
                ? "Email me a six-digit code"
                : "Send sign-in link"}
          </PrimaryAuthButton>
        </form>
        <p
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: 13,
            color: "#9ca3af",
            textAlign: "center",
            marginTop: 18,
          }}
        >
          <AuthLink onClick={() => setStep("select-method")}>
            Other sign-in options
          </AuthLink>
        </p>
      </AuthShell>
    );
  }

  /* Default: select method */
  return (
    <AuthShell title="Sign in to 172X Pipeline" onBack={goBack}>
      {showSessionBanner && (
        <SessionExpiredBanner onDismiss={() => setShowSessionBanner(false)} />
      )}

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
          marginBottom: 4,
        }}
      >
        <ProviderButton
          provider="google"
          onClick={() => void handleOAuth("google")}
          disabled={googleDisabled}
          disabledReason={providerDisabledReason(
            "Google",
            providerStatusPending,
            configurationError,
          )}
        />
        <ProviderButton
          provider="linkedin"
          onClick={() => void handleOAuth("linkedin")}
          disabled={linkedinDisabled}
          disabledReason={providerDisabledReason(
            "LinkedIn",
            providerStatusPending,
            configurationError,
          )}
        />
      </div>

      <OrDivider />

      <PrimaryAuthButton
        onClick={() => setStep("enter-email")}
        disabled={emailDisabled}
      >
        Continue with Email
      </PrimaryAuthButton>

      {configurationError && <AuthConfigurationError />}
      {/* Auth error (generic, no sensitive info) */}
      {step === "auth-error" && (
        <div
          role="alert"
          style={{
            marginTop: 16,
            padding: "12px 14px",
            background: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: 8,
          }}
        >
          <p
            style={{
              fontFamily: "Inter, sans-serif",
              fontSize: 13,
              color: "#991b1b",
              margin: 0,
            }}
          >
            Sign-in failed. Please try again or use a different method.
          </p>
        </div>
      )}
    </AuthShell>
  );
}

function signInOtpProblem(error: ApiClientError): string {
  if (error.problem.status === 403) {
    return "This account does not currently have Alpha access.";
  }
  return error.problem.detail;
}

function providerDisabledReason(
  provider: "Google" | "LinkedIn",
  pending: boolean,
  configurationError: boolean,
): string {
  if (pending) return "Checking provider availability.";
  if (configurationError) {
    return "Provider availability could not be verified.";
  }
  return `${provider} sign-in is not configured for this environment.`;
}

function AuthConfigurationError() {
  return (
    <p
      role="alert"
      style={{
        color: "#991b1b",
        fontFamily: "Inter, sans-serif",
        fontSize: 12.5,
        lineHeight: 1.5,
        marginTop: 14,
        textAlign: "center",
      }}
    >
      Sign-in methods could not be verified. Check the API connection and retry.
    </p>
  );
}
